import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Building2, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  ArrowRight,
  PartyPopper
} from "lucide-react";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { user, token, getAuthHeader } = useAuth();
  
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    } else if (!sessionId) {
      setStatus("error");
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/checkout/status/${sessionId}`,
        { headers: getAuthHeader() }
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(response.data);
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      // Continue polling even on error
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="text-center">
            <Loader2 className="w-16 h-16 animate-spin text-emerald-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Processing Your Payment</h2>
            <p className="text-slate-600">Please wait while we confirm your payment...</p>
            <p className="text-sm text-slate-400 mt-4">Attempt {attempts + 1} of 10</p>
          </div>
        );

      case "success":
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <PartyPopper className="w-6 h-6 text-amber-500" />
              <h2 className="text-3xl font-bold text-slate-900">Payment Successful!</h2>
              <PartyPopper className="w-6 h-6 text-amber-500" />
            </div>
            <p className="text-lg text-slate-600 mb-2">
              Thank you for subscribing to HotelBudget!
            </p>
            {paymentData?.plan && (
              <p className="text-emerald-600 font-medium mb-6">
                Your {paymentData.plan === "premium" ? "Premium" : "Basic"} plan is now active.
              </p>
            )}
            {paymentData?.expires_at && (
              <p className="text-sm text-slate-500 mb-8">
                Access expires: {new Date(paymentData.expires_at).toLocaleDateString()}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-8">
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/properties">
                <Button variant="outline" className="px-8">
                  Create Your Property
                </Button>
              </Link>
            </div>
          </div>
        );

      case "timeout":
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-12 h-12 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Processing</h2>
            <p className="text-slate-600 mb-6">
              Your payment is still being processed. This may take a few minutes.
              Please check your email for confirmation.
            </p>
            <Link to="/">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        );

      case "expired":
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Session Expired</h2>
            <p className="text-slate-600 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/pricing">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Return to Pricing
              </Button>
            </Link>
          </div>
        );

      case "error":
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Something Went Wrong</h2>
            <p className="text-slate-600 mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Link to="/pricing">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Return to Pricing
              </Button>
            </Link>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      {/* Logo */}
      <div className="fixed top-6 left-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            HotelBudget
          </span>
        </Link>
      </div>

      <Card className="w-full max-w-lg border-slate-200 shadow-lg">
        <CardContent className="pt-12 pb-12 px-8">
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
